/**
 * navigation/appShell.tsx
 * The signed-in catalog shell: docked sidebar (overlay drawer on mobile),
 * topbar, command palette, and the content area — which hosts the app
 * navigator (appStack). The sidebar/palette navigate via the container ref
 * (navigationRef) since they render outside the navigator.
 */

import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Platform, TouchableOpacity } from 'react-native';

import { useTheme } from '../context/themeContext';
import { useResponsive } from '../lib/responsive';
import Sidebar from '../shell/sidebar';
import Topbar from '../shell/topbar';
import AppStack from './appStack';

interface AppShellProps {
  /** Sign out → root navigator hands off to the platform auth. */
  onSignOut: () => void;
}

export default function AppShell({ onSignOut }: AppShellProps) {
  const { theme } = useTheme();
  const { colors } = theme;

  // Below the mobile breakpoint the docked sidebar would crush the content, so
  // we switch to a hamburger-triggered overlay drawer (phones + small tablets).
  const { isMobile } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => { if (!isMobile && drawerOpen) setDrawerOpen(false); }, [isMobile, drawerOpen]);

  return (
    <View style={[styles.app, { backgroundColor: colors.paper }]}>
      {/* Docked sidebar — wide screens only. */}
      {!isMobile && <Sidebar onLogout={onSignOut} />}

      <View style={styles.main}>
        <Topbar showMenu={isMobile} onMenu={() => setDrawerOpen(true)} />
        <View style={[styles.content, { backgroundColor: colors.paper }]}>
          <AppStack />
        </View>
      </View>

      {/* Mobile overlay drawer — sidebar slides over the content. */}
      {isMobile && drawerOpen && (
        <View style={styles.overlay}>
          <Sidebar
            mobile
            onLogout={() => { setDrawerOpen(false); onSignOut(); }}
            onNavigate={() => setDrawerOpen(false)}
          />
          <TouchableOpacity
            style={styles.scrim}
            activeOpacity={1}
            onPress={() => setDrawerOpen(false)}
            accessibilityLabel="Close menu"
          />
        </View>
      )}

    </View>
  );
}

const styles = StyleSheet.create({
  app: {
    flex: 1,
    flexDirection: 'row',
    ...(Platform.OS === 'web' ? { height: '100vh' as any, overflow: 'hidden' as any } : { flex: 1 }),
  },
  main: {
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
    ...(Platform.OS === 'web' ? { overflow: 'hidden' as any } : {}),
  },
  content: {
    flex: 1,
    ...(Platform.OS === 'web' ? { overflowY: 'auto' as any } : {}),
  },
  contentNoPad: { padding: 0 },
  overlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    flexDirection: 'row',
    ...(Platform.OS === 'web' ? { zIndex: 100 as any } : {}),
  },
  scrim: { flex: 1, backgroundColor: 'rgba(15,23,42,0.45)' },
});
